// src/components/DocSourcesPanel.js
import React, { useEffect, useMemo, useRef, useState } from "react";

/**
 * DocSourcesPanel
 *
 * Local-only source preparation for Doc Writer.
 *
 * Responsibilities:
 * - Select browser files.
 * - Validate supported types, file count, file size, and declared page limits.
 * - Detect or estimate page counts when reasonably possible.
 * - Request a manual page count when detection is unavailable.
 * - Preserve the original browser File for launchDocwriter().
 *
 * This component does not authenticate, upload, or call an API.
 *
 * Preferred usage:
 *
 *   <DocSourcesPanel
 *     items={sourceItems}
 *     onChange={setSourceItems}
 *     canUpload={isLoggedIn}
 *     onValidityChange={(valid, summary) => setSourcesValid(valid)}
 *   />
 *
 * Compatibility:
 * Existing parents may continue passing onUploadFiles, onRemove, and onPreview.
 * onUploadFiles receives prepared/upsertable items that include rawFile.
 */

const MB = 1024 * 1024;

const DEFAULT_LIMITS = Object.freeze({
  maxFiles: 5,
  maxFileMb: 15,
  maxTotalUploadMb: 40,
  maxPagesPerFile: 25,
  maxTotalPages: 50,
});

const MIME_BY_EXTENSION = Object.freeze({
  pdf: "application/pdf",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  csv: "text/csv",
  txt: "text/plain",
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  webp: "image/webp",
});

const ALLOWED_EXTENSIONS = Object.freeze(Object.keys(MIME_BY_EXTENSION));

const FILE_INPUT_ACCEPT = [
  ".pdf",
  ".docx",
  ".xlsx",
  ".csv",
  ".txt",
  ".png",
  ".jpg",
  ".jpeg",
  ".webp",
].join(",");

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, Number(value || 0)));
}

function formatBytes(bytes) {
  const value = Math.max(0, Number(bytes || 0));

  if (value >= MB) {
    return `${(value / MB).toFixed(value >= 10 * MB ? 1 : 2)} MB`;
  }

  if (value >= 1024) {
    return `${(value / 1024).toFixed(1)} KB`;
  }

  return `${value} B`;
}

function getExtension(filename = "") {
  const normalized = String(filename || "").trim().toLowerCase();
  if (!normalized.includes(".")) return "";
  return normalized.slice(normalized.lastIndexOf(".") + 1);
}

function expectedMimeForFile(file) {
  return MIME_BY_EXTENSION[getExtension(file?.name)] || "";
}

function isPdfFile(file) {
  return getExtension(file?.name) === "pdf";
}

function isImageFile(file) {
  return ["png", "jpg", "jpeg", "webp"].includes(getExtension(file?.name));
}

function isTextFile(file) {
  return ["txt", "csv"].includes(getExtension(file?.name));
}

function needsManualPageCountByType(file) {
  return ["docx", "xlsx"].includes(getExtension(file?.name));
}

function makeSourceId(file) {
  const name = String(file?.name || "source");
  const size = Number(file?.size || 0);
  const modified = Number(file?.lastModified || 0);

  return `source_${name}_${size}_${modified}`
    .replace(/[^A-Za-z0-9._-]+/g, "_")
    .slice(0, 220);
}

function sourceSignature(fileOrItem) {
  const file =
    fileOrItem?.rawFile ||
    fileOrItem?.file ||
    fileOrItem?.originalFile ||
    fileOrItem;

  return [
    String(fileOrItem?.name || file?.name || "").toLowerCase(),
    Number(fileOrItem?.size ?? file?.size ?? 0),
    Number(file?.lastModified || 0),
  ].join("::");
}

function normalizePageCountInput(value) {
  const parsed = Number(String(value ?? "").replace(/[^0-9]/g, ""));
  if (!Number.isFinite(parsed) || parsed < 1) return 0;
  return Math.trunc(parsed);
}

function readPageCount(item) {
  return normalizePageCountInput(
    item?.pageCount ??
      item?.page_count ??
      item?.pages ??
      item?.manualPageCount ??
      item?.manual_page_count ??
      0
  );
}

function pageCountStatus(item) {
  return String(
    item?.pageCountStatus ||
      item?.page_count_status ||
      (item?.manualPageCountRequired ? "manual_required" : "")
  )
    .trim()
    .toLowerCase();
}

function itemRequiresManualPageCount(item) {
  const status = pageCountStatus(item);

  return Boolean(
    item?.manualPageCountRequired ||
      item?.manual_page_count_required ||
      status === "manual_required" ||
      status === "unknown"
  );
}

function normalizeIncomingItem(item = {}) {
  const rawFile =
    item.rawFile ||
    item.file ||
    item.originalFile ||
    item.sourceFile ||
    null;

  const name = String(item.name || rawFile?.name || "Untitled source");
  const extension = getExtension(name);
  const expectedMime = MIME_BY_EXTENSION[extension] || "";
  const pageCount = readPageCount(item);
  const manualRequired = itemRequiresManualPageCount(item);
  const error = String(item.error || "");

  return {
    ...item,
    id: String(item.id || makeSourceId(rawFile || { name, size: item.size })),
    name,
    size: Number(item.size ?? rawFile?.size ?? 0),
    type: String(item.type || item.mime || expectedMime || rawFile?.type || ""),
    mime: String(item.mime || item.type || expectedMime || rawFile?.type || ""),
    extension,
    rawFile,
    file: rawFile,
    pageCount,
    page_count: pageCount,
    pages: pageCount,
    pageCountStatus:
      item.pageCountStatus ||
      item.page_count_status ||
      (manualRequired ? "manual_required" : pageCount > 0 ? "provided" : "unknown"),
    page_count_status:
      item.page_count_status ||
      item.pageCountStatus ||
      (manualRequired ? "manual_required" : pageCount > 0 ? "provided" : "unknown"),
    pageCountSource:
      item.pageCountSource || item.page_count_source || "",
    page_count_source:
      item.page_count_source || item.pageCountSource || "",
    manualPageCountRequired: manualRequired,
    manual_page_count_required: manualRequired,
    manualPageCount: normalizePageCountInput(
      item.manualPageCount ??
        item.manual_page_count ??
        (manualRequired ? pageCount : 0)
    ),
    manual_page_count: normalizePageCountInput(
      item.manual_page_count ??
        item.manualPageCount ??
        (manualRequired ? pageCount : 0)
    ),
    status:
      item.status ||
      (error
        ? "error"
        : manualRequired && pageCount <= 0
        ? "needs_input"
        : "ready"),
    error,
  };
}

function mergeItems(currentItems, incomingItems) {
  const map = new Map(
    (currentItems || []).map((item) => [
      String(item.id),
      normalizeIncomingItem(item),
    ])
  );

  for (const rawItem of incomingItems || []) {
    const item = normalizeIncomingItem(rawItem);
    const previous = map.get(item.id) || {};
    map.set(item.id, { ...previous, ...item });
  }

  return Array.from(map.values());
}

async function estimatePdfPageCount(file) {
  const buffer = await file.arrayBuffer();
  const text = new TextDecoder("latin1").decode(buffer);
  const matches = text.match(/\/Type\s*\/Page\b/g);
  return Array.isArray(matches) ? matches.length : 0;
}

async function estimateTextPageCount(file) {
  const text = await file.text();
  const charsPerPage = 3000;
  return Math.max(1, Math.ceil(String(text || "").length / charsPerPage));
}

async function getLocalPageInfo(file) {
  try {
    if (isPdfFile(file)) {
      const pages = await estimatePdfPageCount(file);

      if (pages > 0) {
        return {
          pageCount: pages,
          pageCountStatus: "detected",
          pageCountSource: "frontend_pdf_soft_count",
          manualPageCountRequired: false,
        };
      }

      return {
        pageCount: 0,
        pageCountStatus: "manual_required",
        pageCountSource: "frontend_pdf_count_failed",
        manualPageCountRequired: true,
      };
    }

    if (isImageFile(file)) {
      return {
        pageCount: 1,
        pageCountStatus: "detected",
        pageCountSource: "frontend_image_one_page",
        manualPageCountRequired: false,
      };
    }

    if (isTextFile(file)) {
      const pages = await estimateTextPageCount(file);

      return {
        pageCount: pages,
        pageCountStatus: "estimated",
        pageCountSource: "frontend_text_estimate",
        manualPageCountRequired: false,
      };
    }

    if (needsManualPageCountByType(file)) {
      return {
        pageCount: 0,
        pageCountStatus: "manual_required",
        pageCountSource: "manual_user_entry_pending",
        manualPageCountRequired: true,
      };
    }

    return {
      pageCount: 0,
      pageCountStatus: "manual_required",
      pageCountSource: "frontend_page_count_not_supported",
      manualPageCountRequired: true,
    };
  } catch (error) {
    console.warn("[DocSourcesPanel] Page count check failed:", error);

    return {
      pageCount: 0,
      pageCountStatus: "manual_required",
      pageCountSource: "frontend_page_count_error",
      manualPageCountRequired: true,
    };
  }
}

function buildPreviewUrl(file) {
  if (
    !file ||
    typeof URL === "undefined" ||
    typeof URL.createObjectURL !== "function"
  ) {
    return null;
  }

  if (!isPdfFile(file) && !isImageFile(file)) return null;

  try {
    return URL.createObjectURL(file);
  } catch {
    return null;
  }
}

function pageCountDescription(item) {
  const pages = readPageCount(item);
  const status = pageCountStatus(item);

  if (pages <= 0 && itemRequiresManualPageCount(item)) {
    return "Page count required";
  }

  if (pages <= 0) return "Page count unavailable";

  const label = `${pages.toLocaleString()} page${pages === 1 ? "" : "s"}`;

  if (status === "estimated") return `${label} estimated`;

  if (status === "user_provided" || status === "manual_required") {
    return `${label} entered by you`;
  }

  return `${label} detected`;
}

function statusPresentation(item) {
  if (item?.error || item?.status === "error") {
    return { label: "Needs attention", tone: "error" };
  }

  if (itemRequiresManualPageCount(item) && readPageCount(item) <= 0) {
    return { label: "Page count required", tone: "warning" };
  }

  if (item?.status === "processing") {
    return { label: "Checking file", tone: "processing" };
  }

  return { label: "Ready", tone: "ready" };
}

const DocSourcesPanel = ({
  items = [],
  onChange,
  onUploadFiles,
  onRemove,
  onPreview,
  onValidityChange,
  canUpload = false,
  disabled = false,
  maxFiles = DEFAULT_LIMITS.maxFiles,
  maxFileMb = DEFAULT_LIMITS.maxFileMb,
  maxTotalUploadMb = DEFAULT_LIMITS.maxTotalUploadMb,
  maxPagesPerFile = DEFAULT_LIMITS.maxPagesPerFile,
  maxTotalPages = DEFAULT_LIMITS.maxTotalPages,
  className = "",
  style,
}) => {
  const inputRef = useRef(null);
  const ownedPreviewUrlsRef = useRef(new Set());
  const dragDepthRef = useRef(0);

  const [localItems, setLocalItems] = useState(() =>
    (Array.isArray(items) ? items : []).map(normalizeIncomingItem)
  );
  const [message, setMessage] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [preparingCount, setPreparingCount] = useState(0);
  const [manualPageDrafts, setManualPageDrafts] = useState({});
  const [manualPageDraftErrors, setManualPageDraftErrors] = useState({});

  const uploadEnabled = Boolean(canUpload && !disabled);

  useEffect(() => {
    setLocalItems((current) =>
      mergeItems(current, Array.isArray(items) ? items : [])
    );
  }, [items]);

  useEffect(() => {
    const liveIds = new Set(
      localItems.map((item) => String(item?.id || "")).filter(Boolean)
    );

    setManualPageDrafts((current) => {
      let changed = false;
      const next = {};

      for (const [id, value] of Object.entries(current || {})) {
        if (liveIds.has(id)) {
          next[id] = value;
        } else {
          changed = true;
        }
      }

      return changed ? next : current;
    });

    setManualPageDraftErrors((current) => {
      let changed = false;
      const next = {};

      for (const [id, value] of Object.entries(current || {})) {
        if (liveIds.has(id)) {
          next[id] = value;
        } else {
          changed = true;
        }
      }

      return changed ? next : current;
    });
  }, [localItems]);

  useEffect(() => {
    if (!inputRef.current) return;

    inputRef.current.disabled = !uploadEnabled;

    if (!uploadEnabled) {
      try {
        inputRef.current.value = "";
      } catch {}
    }
  }, [uploadEnabled]);

  useEffect(() => {
    return () => {
      for (const url of ownedPreviewUrlsRef.current) {
        try {
          URL.revokeObjectURL(url);
        } catch {}
      }

      ownedPreviewUrlsRef.current.clear();
    };
  }, []);

  const limits = useMemo(
    () => ({
      maxFiles: Math.max(1, Number(maxFiles || DEFAULT_LIMITS.maxFiles)),
      maxFileBytes:
        Math.max(1, Number(maxFileMb || DEFAULT_LIMITS.maxFileMb)) * MB,
      maxFileMb: Math.max(
        1,
        Number(maxFileMb || DEFAULT_LIMITS.maxFileMb)
      ),
      maxTotalBytes:
        Math.max(
          1,
          Number(maxTotalUploadMb || DEFAULT_LIMITS.maxTotalUploadMb)
        ) * MB,
      maxTotalUploadMb: Math.max(
        1,
        Number(maxTotalUploadMb || DEFAULT_LIMITS.maxTotalUploadMb)
      ),
      maxPagesPerFile: Math.max(
        1,
        Number(maxPagesPerFile || DEFAULT_LIMITS.maxPagesPerFile)
      ),
      maxTotalPages: Math.max(
        1,
        Number(maxTotalPages || DEFAULT_LIMITS.maxTotalPages)
      ),
    }),
    [
      maxFiles,
      maxFileMb,
      maxTotalUploadMb,
      maxPagesPerFile,
      maxTotalPages,
    ]
  );

  const effectivePageCount = (item) => {
    const id = String(item?.id || "");

    if (
      id &&
      Object.prototype.hasOwnProperty.call(manualPageDrafts, id)
    ) {
      return normalizePageCountInput(manualPageDrafts[id]);
    }

    return readPageCount(item);
  };

  const summary = useMemo(() => {
    const count = localItems.length;

    const totalBytes = localItems.reduce(
      (sum, item) => sum + Math.max(0, Number(item?.size || 0)),
      0
    );

    const totalPages = localItems.reduce(
      (sum, item) => sum + Math.max(0, effectivePageCount(item)),
      0
    );

    const missingManualPageCounts = localItems.filter(
      (item) =>
        itemRequiresManualPageCount(item) && effectivePageCount(item) <= 0
    ).length;

    const itemErrors = localItems.filter(
      (item) => Boolean(item?.error) || item?.status === "error"
    ).length;

    const overFiles = count > limits.maxFiles;
    const overTotalBytes = totalBytes > limits.maxTotalBytes;
    const overTotalPages = totalPages > limits.maxTotalPages;

    const hasPerFilePageError = localItems.some(
      (item) => effectivePageCount(item) > limits.maxPagesPerFile
    );

    const hasPerFileSizeError = localItems.some(
      (item) => Number(item?.size || 0) > limits.maxFileBytes
    );

    const valid =
      !overFiles &&
      !overTotalBytes &&
      !overTotalPages &&
      !hasPerFilePageError &&
      !hasPerFileSizeError &&
      missingManualPageCounts === 0 &&
      itemErrors === 0 &&
      preparingCount === 0;

    return {
      count,
      totalBytes,
      totalPages,
      missingManualPageCounts,
      itemErrors,
      overFiles,
      overTotalBytes,
      overTotalPages,
      hasPerFilePageError,
      hasPerFileSizeError,
      valid,
      filePercent: clamp((count / limits.maxFiles) * 100, 0, 100),
      bytePercent: clamp(
        (totalBytes / limits.maxTotalBytes) * 100,
        0,
        100
      ),
      pagePercent: clamp(
        (totalPages / limits.maxTotalPages) * 100,
        0,
        100
      ),
    };
  }, [localItems, limits, preparingCount, manualPageDrafts]);

  useEffect(() => {
    onValidityChange?.(summary.valid, {
      ...summary,
      limits: {
        maxFiles: limits.maxFiles,
        maxFileMb: limits.maxFileMb,
        maxTotalUploadMb: limits.maxTotalUploadMb,
        maxPagesPerFile: limits.maxPagesPerFile,
        maxTotalPages: limits.maxTotalPages,
      },
    });
  }, [summary, limits, onValidityChange]);

  const emitAll = (nextItems) => {
    const normalized = nextItems.map(normalizeIncomingItem);
    setLocalItems(normalized);
    onChange?.(normalized);
  };

  const emitUpsert = (nextItem) => {
    const normalized = normalizeIncomingItem(nextItem);

    setLocalItems((current) => {
      const next = mergeItems(current, [normalized]);
      onChange?.(next);
      return next;
    });

    onUploadFiles?.([normalized]);
  };

  const removeItem = (id) => {
    const target = localItems.find((item) => item.id === id);

    if (
      target?.previewUrl &&
      ownedPreviewUrlsRef.current.has(target.previewUrl)
    ) {
      try {
        URL.revokeObjectURL(target.previewUrl);
      } catch {}

      ownedPreviewUrlsRef.current.delete(target.previewUrl);
    }

    const next = localItems.filter((item) => item.id !== id);
    emitAll(next);
    onRemove?.(id);
    setManualPageDrafts((current) => {
      if (!Object.prototype.hasOwnProperty.call(current, id)) return current;
      const nextDrafts = { ...current };
      delete nextDrafts[id];
      return nextDrafts;
    });
    setManualPageDraftErrors((current) => {
      if (!Object.prototype.hasOwnProperty.call(current, id)) return current;
      const nextErrors = { ...current };
      delete nextErrors[id];
      return nextErrors;
    });
    setMessage(null);
  };

  const openPreview = (item) => {
    if (typeof onPreview === "function") {
      onPreview(item.id);
      return;
    }

    if (item?.previewUrl) {
      window.open(item.previewUrl, "_blank", "noopener,noreferrer");
    }
  };

  const validateSelectedFile = (file, projectedItems) => {
    const extension = getExtension(file?.name);

    if (!ALLOWED_EXTENSIONS.includes(extension)) {
      return `“${file?.name || "This file"}” is not a supported file type.`;
    }

    if (Number(file?.size || 0) <= 0) {
      return `“${file?.name || "This file"}” is empty or its size could not be read.`;
    }

    if (Number(file.size) > limits.maxFileBytes) {
      return `“${file.name}” is too large. Each file may be up to ${limits.maxFileMb} MB.`;
    }

    if (projectedItems.length >= limits.maxFiles) {
      return `Doc Writer allows up to ${limits.maxFiles} supporting files.`;
    }

    const projectedBytes =
      projectedItems.reduce(
        (sum, item) => sum + Math.max(0, Number(item?.size || 0)),
        0
      ) + Number(file.size || 0);

    if (projectedBytes > limits.maxTotalBytes) {
      return `The combined files would exceed ${limits.maxTotalUploadMb} MB.`;
    }

    const signature = sourceSignature(file);

    if (
      projectedItems.some((item) => sourceSignature(item) === signature)
    ) {
      return `“${file.name}” has already been selected.`;
    }

    return "";
  };

  const prepareSelectedFiles = async (fileList) => {
    if (!uploadEnabled) return;

    const selectedFiles = Array.from(fileList || []);
    if (!selectedFiles.length) return;

    setMessage(null);
    setPreparingCount((count) => count + selectedFiles.length);

    const prepared = [];
    let projectedItems = [...localItems];
    const rejectedMessages = [];

    for (const file of selectedFiles) {
      const validationError = validateSelectedFile(file, projectedItems);

      if (validationError) {
        rejectedMessages.push(validationError);
        setPreparingCount((count) => Math.max(0, count - 1));
        continue;
      }

      const id = makeSourceId(file);
      const expectedMime = expectedMimeForFile(file);
      const previewUrl = buildPreviewUrl(file);

      if (previewUrl) {
        ownedPreviewUrlsRef.current.add(previewUrl);
      }

      const pendingItem = normalizeIncomingItem({
        id,
        name: file.name,
        size: file.size,
        type: expectedMime,
        mime: expectedMime,
        rawFile: file,
        file,
        previewUrl,
        status: "processing",
        pageCount: 0,
        pageCountStatus: "checking",
        pageCountSource: "frontend_check_pending",
        manualPageCountRequired: false,
        error: "",
      });

      projectedItems = [...projectedItems, pendingItem];
      setLocalItems(projectedItems);

      const pageInfo = await getLocalPageInfo(file);
      const pageCount = normalizePageCountInput(pageInfo.pageCount);
      let error = "";

      if (pageCount > limits.maxPagesPerFile) {
        error = `This file has ${pageCount.toLocaleString()} pages. Doc Writer allows up to ${limits.maxPagesPerFile} pages per file.`;
      }

      const nextTotalPages =
        projectedItems
          .filter((item) => item.id !== id)
          .reduce((sum, item) => sum + readPageCount(item), 0) +
        pageCount;

      if (!error && nextTotalPages > limits.maxTotalPages) {
        error = `Adding this file would exceed the ${limits.maxTotalPages}-page combined limit.`;
      }

      const readyItem = normalizeIncomingItem({
        ...pendingItem,
        ...pageInfo,
        pageCount,
        page_count: pageCount,
        pages: pageCount,
        manualPageCount:
          pageInfo.manualPageCountRequired ? 0 : pageCount,
        manual_page_count:
          pageInfo.manualPageCountRequired ? 0 : pageCount,
        status: error
          ? "error"
          : pageInfo.manualPageCountRequired
          ? "needs_input"
          : "ready",
        error,
        note: pageCountDescription({
          ...pendingItem,
          ...pageInfo,
          pageCount,
        }),
      });

      projectedItems = projectedItems.map((item) =>
        item.id === id ? readyItem : item
      );

      prepared.push(readyItem);
      setLocalItems(projectedItems);
      setPreparingCount((count) => Math.max(0, count - 1));
    }

    if (prepared.length) {
      onChange?.(projectedItems);
      onUploadFiles?.(prepared);
    }

    if (rejectedMessages.length) {
      setMessage({
        type: "error",
        title: "Some files were not added",
        lines: rejectedMessages,
      });
    } else if (prepared.length) {
      setMessage({
        type: "success",
        title: `${prepared.length} file${
          prepared.length === 1 ? "" : "s"
        } added`,
        lines: [
          "Review the page counts below before generating your document.",
        ],
      });
    }

    try {
      if (inputRef.current) inputRef.current.value = "";
    } catch {}
  };

  const manualDraftValue = (item) => {
    const id = String(item?.id || "");

    if (
      id &&
      Object.prototype.hasOwnProperty.call(manualPageDrafts, id)
    ) {
      return String(manualPageDrafts[id] ?? "");
    }

    const pages = readPageCount(item);
    return pages > 0 ? String(pages) : "";
  };

  const buildManualPageItem = (item, pages) => {
    let error = "";

    const otherPages = localItems
      .filter((candidate) => candidate.id !== item.id)
      .reduce((sum, candidate) => sum + effectivePageCount(candidate), 0);

    if (
      pages > 0 &&
      otherPages + pages > limits.maxTotalPages
    ) {
      error = `The combined source package may contain up to ${limits.maxTotalPages} pages.`;
    }

    return normalizeIncomingItem({
      ...item,
      pageCount: pages,
      page_count: pages,
      pages,
      manualPageCount: pages,
      manual_page_count: pages,
      manualPageCountRequired: true,
      manual_page_count_required: true,
      pageCountStatus: pages > 0 ? "user_provided" : "manual_required",
      page_count_status:
        pages > 0 ? "user_provided" : "manual_required",
      pageCountSource:
        pages > 0
          ? "manual_user_entry"
          : "manual_user_entry_pending",
      page_count_source:
        pages > 0
          ? "manual_user_entry"
          : "manual_user_entry_pending",
      status: error
        ? "error"
        : pages > 0
        ? "ready"
        : "needs_input",
      error,
      note:
        pages > 0
          ? `${pages} page${pages === 1 ? "" : "s"} entered by you`
          : "",
    });
  };

  const updateManualPageDraft = (item, value) => {
    const id = String(item?.id || "");
    if (!id) return;

    const raw = String(value ?? "").replace(/[^0-9]/g, "");

    if (raw === "") {
      setManualPageDrafts((current) => ({ ...current, [id]: "" }));
      setManualPageDraftErrors((current) => ({
        ...current,
        [id]: "",
      }));

      const updated = buildManualPageItem(item, 0);
      setLocalItems((current) =>
        current.map((candidate) =>
          candidate.id === item.id ? updated : candidate
        )
      );
      return;
    }

    const pages = normalizePageCountInput(raw);

    if (pages > limits.maxPagesPerFile) {
      setManualPageDraftErrors((current) => ({
        ...current,
        [id]: `Enter a page count from 1 to ${limits.maxPagesPerFile}.`,
      }));
      return;
    }

    setManualPageDrafts((current) => ({ ...current, [id]: raw }));
    setManualPageDraftErrors((current) => ({
      ...current,
      [id]: "",
    }));

    const updated = buildManualPageItem(item, pages);

    // Update only this component while the user is typing. Do not emit to the
    // parent on every digit; that parent round-trip is what made the field
    // jump/lose focus after entering the first digit.
    setLocalItems((current) =>
      current.map((candidate) =>
        candidate.id === item.id ? updated : candidate
      )
    );
  };

  const commitManualPageCount = (item) => {
    const id = String(item?.id || "");
    if (!id) return;

    const raw = manualDraftValue(item);
    const pages = normalizePageCountInput(raw);

    if (pages < 1 || pages > limits.maxPagesPerFile) {
      setManualPageDraftErrors((current) => ({
        ...current,
        [id]: `Enter a page count from 1 to ${limits.maxPagesPerFile}.`,
      }));
      return;
    }

    const latest =
      localItems.find((candidate) => candidate.id === item.id) || item;
    const updated = buildManualPageItem(latest, pages);

    setManualPageDraftErrors((current) => ({
      ...current,
      [id]: "",
    }));

    // Commit once the user finishes editing. At this point the Start Page gets
    // the numeric value (for example "05" -> 5) and includes it in the full
    // source-package page total.
    emitUpsert(updated);
  };

  const handleDragEnter = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (!uploadEnabled) return;

    dragDepthRef.current += 1;
    setDragActive(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();

    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);

    if (dragDepthRef.current === 0) {
      setDragActive(false);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();

    dragDepthRef.current = 0;
    setDragActive(false);

    if (!uploadEnabled) return;

    prepareSelectedFiles(event.dataTransfer?.files);
  };

  const addButtonDisabled =
    !uploadEnabled ||
    preparingCount > 0 ||
    summary.count >= limits.maxFiles ||
    summary.totalBytes >= limits.maxTotalBytes ||
    summary.totalPages >= limits.maxTotalPages;

  const overallTone =
    summary.overFiles ||
    summary.overTotalBytes ||
    summary.overTotalPages ||
    summary.itemErrors > 0
      ? "error"
      : summary.missingManualPageCounts > 0
      ? "warning"
      : "normal";

  return (
    <section
      className={`doc-sources-panel ${className}`.trim()}
      style={{ ...styles.wrap, ...(style || {}) }}
      aria-label="Source files"
    >
      <style>{`
        .doc-sources-panel * {
          box-sizing: border-box;
        }

        .doc-sources-panel button,
        .doc-sources-panel input {
          font: inherit;
        }

        .doc-sources-panel button:focus-visible,
        .doc-sources-panel input:focus-visible {
          outline: 3px solid rgba(30, 42, 120, 0.20);
          outline-offset: 2px;
        }

        .doc-sources-file-grid {
          grid-template-columns: minmax(0, 1fr) auto;
        }

        .doc-sources-limit-grid {
          grid-template-columns: repeat(4, minmax(0, 1fr));
        }

        @media (max-width: 760px) {
          .doc-sources-limit-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
          }

          .doc-sources-file-grid {
            grid-template-columns: minmax(0, 1fr) !important;
          }

          .doc-sources-file-actions {
            justify-content: flex-start !important;
          }
        }

        @media (max-width: 460px) {
          .doc-sources-limit-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>

      <div style={styles.header}>
        <div style={styles.headerText}>
          <p style={styles.subtitle}>
            Add contracts, letters, records, spreadsheets, images, or other
            material that should guide the draft.
          </p>
        </div>

        <button
          type="button"
          style={{
            ...styles.headerButton,
            ...(addButtonDisabled ? styles.disabledButton : null),
          }}
          onClick={() => inputRef.current?.click()}
          disabled={addButtonDisabled}
        >
          <PaperclipIcon />
          Choose files
        </button>
      </div>

      <div className="doc-sources-limit-grid" style={styles.limitGrid}>
        <LimitCard
          label="Files"
          value={`${summary.count} of ${limits.maxFiles}`}
          percent={summary.filePercent}
          tone={summary.overFiles ? "error" : "normal"}
        />

        <LimitCard
          label="Total size"
          value={`${formatBytes(summary.totalBytes)} of ${
            limits.maxTotalUploadMb
          } MB`}
          percent={summary.bytePercent}
          tone={summary.overTotalBytes ? "error" : "normal"}
        />

        <LimitCard
          label="Total pages"
          value={`${summary.totalPages} of ${limits.maxTotalPages}`}
          percent={summary.pagePercent}
          tone={summary.overTotalPages ? "error" : "normal"}
        />

        <LimitCard
          label="Per file"
          value={`${limits.maxFileMb} MB · ${limits.maxPagesPerFile} pages`}
          percent={0}
          tone="normal"
          hideBar
        />
      </div>

      <div
        role="button"
        tabIndex={uploadEnabled ? 0 : -1}
        aria-disabled={!uploadEnabled}
        onClick={() => {
          if (!addButtonDisabled) inputRef.current?.click();
        }}
        onKeyDown={(event) => {
          if (
            !addButtonDisabled &&
            (event.key === "Enter" || event.key === " ")
          ) {
            event.preventDefault();
            inputRef.current?.click();
          }
        }}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        style={{
          ...styles.dropZone,
          ...(dragActive ? styles.dropZoneActive : null),
          ...(addButtonDisabled ? styles.dropZoneDisabled : null),
        }}
      >
        <div style={styles.dropIcon}>
          <UploadIcon />
        </div>

        <div style={styles.dropTitle}>
          {preparingCount > 0
            ? `Checking ${preparingCount} file${
                preparingCount === 1 ? "" : "s"
              }…`
            : "Drag files here or choose from your device"}
        </div>

        <div style={styles.dropText}>
          PDF, DOCX, XLSX, CSV, TXT, PNG, JPG, and WEBP
        </div>

        <div style={styles.dropLimits}>
          Up to {limits.maxFiles} files · {limits.maxFileMb} MB each ·{" "}
          {limits.maxTotalUploadMb} MB combined
        </div>
      </div>

      <input
        ref={inputRef}
        type="file"
        multiple
        accept={FILE_INPUT_ACCEPT}
        onChange={(event) => prepareSelectedFiles(event?.target?.files)}
        style={{ display: "none" }}
      />

      <div style={styles.pdfNotice}>
        <div style={styles.pdfNoticeIcon}>PDF</div>

        <div>
          <div style={styles.pdfNoticeTitle}>PDF is recommended</div>
          <div style={styles.pdfNoticeText}>
            PDF files provide the most reliable page count and preserve
            document layout. Word, spreadsheet, and text files are accepted,
            but you may be asked to confirm their page count.
          </div>
        </div>
      </div>

      {message ? (
        <PanelMessage
          type={message.type}
          title={message.title}
          lines={message.lines}
          onClose={() => setMessage(null)}
        />
      ) : null}

      {localItems.length === 0 ? (
        <div style={styles.emptyState}>
          <div style={styles.emptyIcon}>
            <FileIcon />
          </div>

          <div>
            <div style={styles.emptyTitle}>
              No supporting files added
            </div>
            <div style={styles.emptyText}>
              You may continue without attachments or add files that should
              guide the document.
            </div>
          </div>
        </div>
      ) : (
        <div style={styles.fileList}>
          {localItems.map((item) => {
            const presentation = statusPresentation(item);
            const manualRequired = itemRequiresManualPageCount(item);
            const pages = readPageCount(item);
            const canPreview = Boolean(item.previewUrl);

            return (
              <article key={item.id} style={styles.fileCard}>
                <div
                  className="doc-sources-file-grid"
                  style={styles.fileGrid}
                >
                  <div style={styles.fileMain}>
                    <div style={styles.fileIconWrap}>
                      <FileTypeBadge extension={item.extension} />
                    </div>

                    <div style={styles.fileIdentity}>
                      <div style={styles.fileTopLine}>
                        <div style={styles.fileName} title={item.name}>
                          {item.name}
                        </div>

                        <span
                          style={{
                            ...styles.statusBadge,
                            ...statusBadgeStyle(presentation.tone),
                          }}
                        >
                          {presentation.label}
                        </span>
                      </div>

                      <div style={styles.fileMeta}>
                        <span>
                          {String(item.extension || "file").toUpperCase()}
                        </span>
                        <span aria-hidden="true">•</span>
                        <span>{formatBytes(item.size)}</span>
                        <span aria-hidden="true">•</span>
                        <span>{pageCountDescription(item)}</span>
                      </div>

                      {item.error ? (
                        <div role="alert" style={styles.fileError}>
                          {item.error}
                        </div>
                      ) : null}
                    </div>
                  </div>

                  <div
                    className="doc-sources-file-actions"
                    style={styles.fileActions}
                  >
                    {canPreview ? (
                      <button
                        type="button"
                        style={styles.secondaryButton}
                        onClick={() => openPreview(item)}
                      >
                        Preview
                      </button>
                    ) : null}

                    <button
                      type="button"
                      style={styles.removeButton}
                      onClick={() => removeItem(item.id)}
                      disabled={disabled}
                    >
                      Remove
                    </button>
                  </div>
                </div>

                {manualRequired ? (
                  <div style={styles.manualBox}>
                    <div style={styles.manualIcon}>!</div>

                    <div style={styles.manualContent}>
                      <div style={styles.manualTitle}>
                        Page count required
                      </div>

                      <div style={styles.manualText}>
                        We could not reliably determine the number of pages in
                        this{" "}
                        {item.extension?.toUpperCase() || "document"} file.
                        Enter the page count shown in the document.
                      </div>

                      <label style={styles.manualLabel}>
                        Number of pages
                        <input
                          type="number"
                          inputMode="numeric"
                          min="1"
                          max={limits.maxPagesPerFile}
                          step="1"
                          value={manualDraftValue(item)}
                          placeholder={`1–${limits.maxPagesPerFile}`}
                          onChange={(event) =>
                            updateManualPageDraft(
                              item,
                              event.target.value
                            )
                          }
                          onBlur={() => commitManualPageCount(item)}
                          onKeyDown={(event) => {
                            if (event.key === "Enter") {
                              event.preventDefault();
                              commitManualPageCount(item);
                            }
                          }}
                          disabled={disabled}
                          style={{
                            ...styles.manualInput,
                            ...(item.error ||
                            manualPageDraftErrors[String(item.id || "")]
                              ? styles.manualInputError
                              : null),
                          }}
                        />
                      </label>

                      {manualPageDraftErrors[String(item.id || "")] ? (
                        <div role="alert" style={styles.manualInputMessage}>
                          {manualPageDraftErrors[String(item.id || "")]}
                        </div>
                      ) : null}

                      <div style={styles.manualFoot}>
                        For the most reliable page count and formatting,
                        upload a PDF version when available.
                      </div>
                    </div>
                  </div>
                ) : null}
              </article>
            );
          })}
        </div>
      )}

      <div
        style={{
          ...styles.summaryBar,
          ...(overallTone === "warning"
            ? styles.summaryBarWarning
            : null),
          ...(overallTone === "error"
            ? styles.summaryBarError
            : null),
        }}
      >
        <div>
          <strong>
            {summary.count} of {limits.maxFiles} files
          </strong>
          <span style={styles.summarySeparator}>·</span>
          <span>
            {formatBytes(summary.totalBytes)} of{" "}
            {limits.maxTotalUploadMb} MB
          </span>
          <span style={styles.summarySeparator}>·</span>
          <span>
            {summary.totalPages} of {limits.maxTotalPages} pages
          </span>
        </div>

        <div style={styles.summaryMessage}>
          {summary.missingManualPageCounts > 0
            ? `Enter the page count for ${
                summary.missingManualPageCounts
              } file${
                summary.missingManualPageCounts === 1 ? "" : "s"
              } before generating.`
            : summary.itemErrors > 0
            ? "Resolve the file errors before generating."
            : preparingCount > 0
            ? "Checking selected files…"
            : "Source files are ready."}
        </div>
      </div>
    </section>
  );
};

function LimitCard({
  label,
  value,
  percent,
  tone,
  hideBar = false,
}) {
  return (
    <div style={styles.limitCard}>
      <div style={styles.limitLabel}>{label}</div>

      <div
        style={{
          ...styles.limitValue,
          ...(tone === "error" ? styles.limitValueError : null),
        }}
      >
        {value}
      </div>

      {!hideBar ? (
        <div style={styles.limitTrack} aria-hidden="true">
          <div
            style={{
              ...styles.limitFill,
              ...(tone === "error"
                ? styles.limitFillError
                : null),
              width: `${clamp(percent, 0, 100)}%`,
            }}
          />
        </div>
      ) : (
        <div style={styles.limitSpacer} />
      )}
    </div>
  );
}

function PanelMessage({
  type,
  title,
  lines,
  onClose,
}) {
  const tone =
    type === "error"
      ? styles.messageError
      : type === "success"
      ? styles.messageSuccess
      : styles.messageInfo;

  return (
    <div role="status" style={{ ...styles.message, ...tone }}>
      <div style={styles.messageContent}>
        <div style={styles.messageTitle}>{title}</div>

        {(lines || []).map((line, index) => (
          <div key={index} style={styles.messageLine}>
            {line}
          </div>
        ))}
      </div>

      <button
        type="button"
        aria-label="Dismiss message"
        style={styles.messageClose}
        onClick={onClose}
      >
        ×
      </button>
    </div>
  );
}

function FileTypeBadge({ extension }) {
  const label = String(extension || "FILE").toUpperCase().slice(0, 5);
  return <span style={styles.fileTypeBadge}>{label}</span>;
}

function statusBadgeStyle(tone) {
  if (tone === "error") return styles.statusError;
  if (tone === "warning") return styles.statusWarning;
  if (tone === "processing") return styles.statusProcessing;
  return styles.statusReady;
}

function PaperclipIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="17"
      height="17"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M21.44 11.05 12 20.5a6 6 0 1 1-8.49-8.49l10-10a4 4 0 0 1 5.66 5.66l-10 10a2 2 0 1 1-2.83-2.83l9.19-9.19" />
    </svg>
  );
}

function UploadIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 3v12" />
      <path d="m7 8 5-5 5 5" />
      <path d="M5 21h14" />
    </svg>
  );
}

function FileIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="21"
      height="21"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" />
    </svg>
  );
}

const styles = {
  wrap: {
    width: "100%",
    border: "1px solid rgba(15, 23, 42, 0.12)",
    borderRadius: 16,
    background: "#FFFFFF",
    padding: 18,
    boxShadow: "0 8px 24px rgba(15, 23, 42, 0.05)",
  },

  header: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 16,
    flexWrap: "wrap",
  },

  headerText: {
    flex: "1 1 520px",
    minWidth: 0,
  },

  eyebrow: {
    marginBottom: 5,
    color: "#1E2A78",
    fontSize: 12,
    fontWeight: 900,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
  },

  title: {
    margin: 0,
    color: "#0F172A",
    fontSize: 21,
    lineHeight: 1.25,
    fontWeight: 900,
  },

  subtitle: {
    margin: "7px 0 0",
    color: "#475569",
    fontSize: 13,
    lineHeight: 1.55,
  },

  headerButton: {
    minHeight: 42,
    border: "1px solid #1E2A78",
    borderRadius: 10,
    background: "#1E2A78",
    color: "#FFFFFF",
    padding: "10px 14px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 900,
  },

  disabledButton: {
    opacity: 0.52,
    cursor: "not-allowed",
  },

  limitGrid: {
    marginTop: 16,
    display: "grid",
    gap: 10,
  },

  limitCard: {
    minWidth: 0,
    border: "1px solid rgba(15, 23, 42, 0.09)",
    borderRadius: 12,
    background: "#F8FAFC",
    padding: 11,
  },

  limitLabel: {
    color: "#64748B",
    fontSize: 11,
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },

  limitValue: {
    marginTop: 4,
    color: "#0F172A",
    fontSize: 13,
    fontWeight: 900,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  limitValueError: {
    color: "#B91C1C",
  },

  limitTrack: {
    height: 5,
    marginTop: 9,
    overflow: "hidden",
    borderRadius: 999,
    background: "#E2E8F0",
  },

  limitFill: {
    height: "100%",
    minWidth: 0,
    borderRadius: 999,
    background: "#1E2A78",
    transition: "width 180ms ease",
  },

  limitFillError: {
    background: "#DC2626",
  },

  limitSpacer: {
    height: 5,
    marginTop: 9,
  },

  dropZone: {
    marginTop: 14,
    minHeight: 174,
    border: "1.5px dashed rgba(30, 42, 120, 0.30)",
    borderRadius: 15,
    background: "#F8FAFF",
    padding: 22,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    cursor: "pointer",
    transition:
      "border-color 160ms ease, background 160ms ease, transform 160ms ease",
  },

  dropZoneActive: {
    borderColor: "#1E2A78",
    background: "#EEF1FF",
    transform: "translateY(-1px)",
  },

  dropZoneDisabled: {
    cursor: "not-allowed",
    opacity: 0.62,
  },

  dropIcon: {
    width: 48,
    height: 48,
    borderRadius: 15,
    background: "#E9EDFF",
    color: "#1E2A78",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },

  dropTitle: {
    marginTop: 11,
    color: "#0F172A",
    fontSize: 15,
    fontWeight: 900,
  },

  dropText: {
    marginTop: 5,
    color: "#475569",
    fontSize: 12,
    fontWeight: 700,
  },

  dropLimits: {
    marginTop: 6,
    color: "#64748B",
    fontSize: 11,
    lineHeight: 1.45,
  },

  pdfNotice: {
    marginTop: 12,
    border: "1px solid rgba(30, 42, 120, 0.13)",
    borderRadius: 12,
    background: "#F7F8FF",
    padding: 12,
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
  },

  pdfNoticeIcon: {
    flex: "0 0 auto",
    borderRadius: 8,
    background: "#1E2A78",
    color: "#FFFFFF",
    padding: "5px 7px",
    fontSize: 10,
    lineHeight: 1,
    fontWeight: 900,
    letterSpacing: "0.04em",
  },

  pdfNoticeTitle: {
    color: "#1E2A78",
    fontSize: 12,
    fontWeight: 900,
  },

  pdfNoticeText: {
    marginTop: 2,
    color: "#475569",
    fontSize: 11,
    lineHeight: 1.48,
  },

  message: {
    marginTop: 12,
    borderRadius: 12,
    padding: 12,
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 10,
  },

  messageInfo: {
    border: "1px solid #BFDBFE",
    background: "#EFF6FF",
    color: "#1E3A8A",
  },

  messageSuccess: {
    border: "1px solid #BBF7D0",
    background: "#F0FDF4",
    color: "#166534",
  },

  messageError: {
    border: "1px solid #FECACA",
    background: "#FEF2F2",
    color: "#991B1B",
  },

  messageContent: {
    minWidth: 0,
  },

  messageTitle: {
    fontSize: 12,
    fontWeight: 900,
  },

  messageLine: {
    marginTop: 3,
    fontSize: 11,
    lineHeight: 1.45,
  },

  messageClose: {
    flex: "0 0 auto",
    border: 0,
    background: "transparent",
    color: "currentColor",
    padding: 0,
    cursor: "pointer",
    fontSize: 20,
    lineHeight: 1,
  },

  emptyState: {
    marginTop: 14,
    border: "1px dashed rgba(15, 23, 42, 0.16)",
    borderRadius: 13,
    background: "#FCFDFE",
    padding: 15,
    display: "flex",
    alignItems: "center",
    gap: 11,
  },

  emptyIcon: {
    width: 40,
    height: 40,
    flex: "0 0 40px",
    borderRadius: 12,
    background: "#EEF2FF",
    color: "#1E2A78",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },

  emptyTitle: {
    color: "#0F172A",
    fontSize: 13,
    fontWeight: 900,
  },

  emptyText: {
    marginTop: 2,
    color: "#64748B",
    fontSize: 11,
    lineHeight: 1.45,
  },

  fileList: {
    marginTop: 14,
    display: "grid",
    gap: 11,
  },

  fileCard: {
    overflow: "hidden",
    border: "1px solid rgba(15, 23, 42, 0.12)",
    borderRadius: 14,
    background: "#FFFFFF",
  },

  fileGrid: {
    display: "grid",
    alignItems: "center",
    gap: 12,
    padding: 12,
  },

  fileMain: {
    minWidth: 0,
    display: "flex",
    alignItems: "flex-start",
    gap: 11,
  },

  fileIconWrap: {
    flex: "0 0 auto",
  },

  fileTypeBadge: {
    minWidth: 44,
    height: 44,
    borderRadius: 12,
    background: "#EEF2FF",
    color: "#1E2A78",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0 7px",
    fontSize: 10,
    fontWeight: 900,
    letterSpacing: "0.03em",
  },

  fileIdentity: {
    minWidth: 0,
    flex: "1 1 auto",
  },

  fileTopLine: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
  },

  fileName: {
    minWidth: 0,
    maxWidth: "100%",
    overflow: "hidden",
    color: "#0F172A",
    fontSize: 13,
    fontWeight: 900,
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  fileMeta: {
    marginTop: 4,
    color: "#64748B",
    fontSize: 11,
    lineHeight: 1.45,
    display: "flex",
    alignItems: "center",
    gap: 5,
    flexWrap: "wrap",
  },

  statusBadge: {
    flex: "0 0 auto",
    borderRadius: 999,
    padding: "4px 7px",
    fontSize: 10,
    lineHeight: 1,
    fontWeight: 900,
  },

  statusReady: {
    background: "#DCFCE7",
    color: "#166534",
  },

  statusWarning: {
    background: "#FEF3C7",
    color: "#92400E",
  },

  statusProcessing: {
    background: "#DBEAFE",
    color: "#1D4ED8",
  },

  statusError: {
    background: "#FEE2E2",
    color: "#B91C1C",
  },

  fileError: {
    marginTop: 7,
    borderRadius: 9,
    background: "#FEF2F2",
    color: "#B91C1C",
    padding: "7px 9px",
    fontSize: 11,
    lineHeight: 1.45,
    fontWeight: 700,
  },

  fileActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 7,
  },

  secondaryButton: {
    border: "1px solid rgba(15, 23, 42, 0.14)",
    borderRadius: 9,
    background: "#FFFFFF",
    color: "#334155",
    padding: "7px 10px",
    cursor: "pointer",
    fontSize: 11,
    fontWeight: 850,
  },

  removeButton: {
    border: "1px solid rgba(185, 28, 28, 0.18)",
    borderRadius: 9,
    background: "#FFF7F7",
    color: "#B91C1C",
    padding: "7px 10px",
    cursor: "pointer",
    fontSize: 11,
    fontWeight: 850,
  },

  manualBox: {
    borderTop: "1px solid rgba(217, 119, 6, 0.16)",
    background: "#FFFBEB",
    padding: 12,
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
  },

  manualIcon: {
    width: 28,
    height: 28,
    flex: "0 0 28px",
    borderRadius: 9,
    background: "#FEF3C7",
    color: "#92400E",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 14,
    fontWeight: 900,
  },

  manualContent: {
    minWidth: 0,
    flex: "1 1 auto",
  },

  manualTitle: {
    color: "#92400E",
    fontSize: 12,
    fontWeight: 900,
  },

  manualText: {
    marginTop: 3,
    color: "#78350F",
    fontSize: 11,
    lineHeight: 1.48,
  },

  manualLabel: {
    marginTop: 10,
    display: "grid",
    gap: 5,
    color: "#0F172A",
    fontSize: 11,
    fontWeight: 850,
  },

  manualInput: {
    width: 150,
    maxWidth: "100%",
    minHeight: 40,
    border: "1px solid rgba(146, 64, 14, 0.24)",
    borderRadius: 10,
    background: "#FFFFFF",
    color: "#0F172A",
    padding: "9px 10px",
    fontSize: 13,
    fontWeight: 800,
  },

  manualInputError: {
    borderColor: "#DC2626",
    boxShadow: "0 0 0 2px rgba(220, 38, 38, 0.08)",
  },

  manualInputMessage: {
    marginTop: 6,
    color: "#B91C1C",
    fontSize: 11,
    lineHeight: 1.4,
    fontWeight: 750,
  },

  manualFoot: {
    marginTop: 7,
    color: "#92400E",
    fontSize: 10,
    lineHeight: 1.42,
  },

  summaryBar: {
    marginTop: 14,
    border: "1px solid rgba(15, 23, 42, 0.10)",
    borderRadius: 12,
    background: "#F8FAFC",
    color: "#334155",
    padding: "11px 12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    flexWrap: "wrap",
    fontSize: 11,
    lineHeight: 1.45,
  },

  summaryBarWarning: {
    borderColor: "#FDE68A",
    background: "#FFFBEB",
    color: "#92400E",
  },

  summaryBarError: {
    borderColor: "#FECACA",
    background: "#FEF2F2",
    color: "#991B1B",
  },

  summarySeparator: {
    margin: "0 6px",
    color: "#94A3B8",
  },

  summaryMessage: {
    fontWeight: 800,
  },
};

export default DocSourcesPanel;
